<template>
  <section id="detail" class="page active">
    <div class="wrap">
      <div class="inner-top">
        <button type="button" class="back" @click="$emit('navigate', 'home')">
          {{ t("detail_back") }}
        </button>
        <div class="row">
          <h1 class="title">
            {{ project.name }}
            <span class="tag">{{ detailTag }}</span>
          </h1>
          <span class="live">{{ t("detail_live") }}</span>
        </div>
        <p class="subtext" v-html="detailSub"></p>
      </div>
      <div class="notice">{{ t("notice_demo") }}</div>
      <div class="detail-grid">
        <div>
          <div class="poster detail-visual">
            <div class="poster-stack">
              <div class="poster-art">
                <img
                  v-if="project.imageUrl"
                  :src="project.imageUrl"
                  :alt="project.name"
                  class="poster-cover-img"
                />
                <i v-else class="poster-logo"></i>
              </div>
              <h3>{{ project.name }}</h3>
              <!-- <span class="english">{{ project.english || project.nameEn }}</span> -->
            </div>
          </div>
          <div class="panel" style="margin-top: 20px">
            <h3>{{ t("about_title") }}</h3>
            <p class="subtext">{{ about1 }}</p>
            <p class="subtext">{{ about2 }}</p>
            <span class="tag">{{ t("tag_film") }}</span>
            <span class="tag">{{ t("tag_nft") }}</span>
            <span class="tag">{{ t("tag_issue") }}</span>
          </div>
        </div>
        <aside class="detail-right">
          <div class="panel">
            <div class="row">
              <h3 style="margin: 0">{{ t("sub_title") }}</h3>
              <span class="tag">{{ modeTag }}</span>
            </div>
            <div ref="coinPriceBox">
              <div class="data-line">
                <span>{{ priceLineLabel }}</span>
                <strong>{{ priceLineValue }}</strong>
              </div>
              <p v-if="project.mode === 'step'" class="price-note">{{ stepNote }}</p>
            </div>
            <div class="data-line">
              <span>{{ t("supply_total") }}</span>
              <strong>{{ supplyTotalV }}</strong>
            </div>
            <div class="data-line">
              <span>{{ t("supply_left") }}</span>
              <strong>{{ supplyLine }}</strong>
            </div>
            <div class="data-line">
              <span>{{ t("target") }}</span>
              <strong>{{ targetValue }}</strong>
            </div>
            <div class="data-line">
              <span>{{ t("my_holding") }}</span>
              <strong>{{ holdingLine }}</strong>
            </div>
            <button
              type="button"
              class="btn gold full"
              style="margin-top: 22px"
              :disabled="soldOut || !project.cardAddress"
              @click="$emit('buy')"
            >
              {{ buyButtonLabel }}
            </button>
            <p class="subtext" style="text-align: center; margin-bottom: 0">
              {{ t("buy_note") }}
            </p>
          </div>
          <div class="panel">
            <h3>{{ t("alloc_title") }}</h3>
            <div class="data-line">
              <span>{{ t("alloc_issuer") }}</span><strong>70%</strong>
            </div>
            <div class="data-line">
              <span>{{ t("alloc_level") }}</span><strong>2% · 最多 15 代</strong>
            </div>
            <p class="subtext">{{ t("alloc_note") }}</p>
            <button type="button" class="btn small" @click="$emit('navigate', 'rules')">
              {{ t("view_rules") }}
            </button>
          </div>
        </aside>
      </div>
      <div class="panel bottom-space">
        <div class="row" style="margin-bottom: 24px">
          <h3 style="margin: 0">{{ t("revenue_title") }}</h3>
          <span class="tag">{{ t("revenue_tag") }}</span>
        </div>
        <div class="distribution">
          <div class="donut">
            <div class="donut-inner">
              <b>100%</b><span>{{ t("donut_label") }}</span>
            </div>
          </div>
          <div class="allocation-list">
            <div v-for="row in allocations" :key="row.key">
              <span>{{ t(row.key) }}</span><strong>{{ row.value }}</strong>
            </div>
          </div>
        </div>
        <div class="notice" style="margin-top: 24px">{{ t("revenue_note") }}</div>
      </div>
    </div>
  </section>
</template>

<script>
import { ALLOCATION_ROWS } from "@/film/allocation";
import {
  calcTotalAt,
  fmtCoin,
  pricingTag,
  projectPricing,
  unitPrice,
} from "@/film/pricing";

export default {
  name: "DetailPage",
  props: {
    t: { type: Function, required: true },
    fmt: { type: Function, required: true },
    lang: { type: String, default: "zh" },
    project: { type: Object, required: true },
    sold: { type: Number, default: 0 },
    holding: { type: Number, default: 0 },
  },
  computed: {
    pricing() {
      return projectPricing(this.project);
    },
    coin() {
      return this.project.coin;
    },
    soldOut() {
      return this.sold >= this.project.supply;
    },
    buyButtonLabel() {
      if (!this.project.cardAddress) return this.t("buy_need_address");
      if (this.soldOut) return this.t("soldout_btn");
      return this.t("buy_btn");
    },
    detailTag() {
      const idx =
        this.project.displayIndex != null ? this.project.displayIndex + 1 : 1;
      return `${this.t("tag_debut")} #${String(idx).padStart(3, "0")}`;
    },
    detailSub() {
      const priceLabel =
        this.project.mode === "fixed"
          ? `${pricingTag(this.pricing, this.t)} · ${fmtCoin(this.project.fixed, this.coin)}`
          : `${pricingTag(this.pricing, this.t)} · ${fmtCoin(this.project.step.start, this.coin)}`;
      return this.fmt("detail_sub", {
        issuer: this.project.issuer,
        supply: this.project.supply.toLocaleString("en-US"),
        coin: this.coin,
        price: priceLabel,
      });
    },
    about1() {
      const about = this.project.about || {};
      if (this.lang === "zh") return about.zh || about.en || "";
      return about.en || about.zh || "";
    },
    about2() {
      const about = this.project.about2 || {};
      if (this.lang === "zh") return about.zh || about.en || "";
      return about.en || about.zh || "";
    },
    modeTag() {
      return `${pricingTag(this.pricing, this.t)} · ${this.coin}`;
    },
    priceLineLabel() {
      return `${this.coin} · ${pricingTag(this.pricing, this.t)}`;
    },
    priceLineValue() {
      if (this.project.mode === "fixed") {
        return `${fmtCoin(this.project.fixed, this.coin)} ${this.coin} / ${this.t("stat2u")}`;
      }
      const next =
        this.project.currentPriceHuman != null
          ? this.project.currentPriceHuman
          : unitPrice(this.pricing, this.sold + 1);
      return `${this.t("next_price")} ${fmtCoin(next, this.coin)} ${this.coin}`;
    },
    stepNote() {
      return this.fmt("step_note_c", {
        start: fmtCoin(this.project.step.start, this.coin),
        inc: fmtCoin(this.project.step.inc, this.coin),
      });
    },
    supplyTotalV() {
      return `${this.project.supply.toLocaleString("en-US")} ${this.t("stat2u")}`;
    },
    supplyLine() {
      const left = this.project.supply - this.sold;
      return `${this.sold} / ${left} ${this.t("stat2u")}`;
    },
    targetValue() {
      const total = calcTotalAt(this.pricing, 0, this.project.supply);
      return `${fmtCoin(total, this.coin)} ${this.coin}`;
    },
    holdingLine() {
      return `${this.holding} ${this.t("stat2u")} NFT`;
    },
    allocations() {
      return ALLOCATION_ROWS.map(([key, value]) => ({ key, value }));
    },
  },
};
</script>
